// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useNavigate } from 'react-router-dom';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { DenyList } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Rbac/Deny';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import useDenyLists from '../../../hooks/queries/registry/denyList/useDenyLists';
import TemplateContract from '../../../utils/TemplateContract';
import { encodeDenyListName } from '../../../utils/urls';
import { PageWrapper } from '../../PageWrapper';

type Row = TemplateContract<DenyList>;

type DenyListsProps = {
  disableVirtualization?: boolean;
};

const createHeaders: () => GridColDef<Row>[] = () => {
  return [
    {
      field: 'name',
      headerName: 'Name',
      valueGetter: (params) => params.row.payload.name,
      flex: 1,
      filterable: true,
    },
    {
      field: 'owner',
      headerName: 'Owner',
      valueGetter: (params) => params.row.payload.owner,
      renderCell: (params) => <PartyDisplay party={params.row.payload.owner} />,
      flex: 1,
      filterable: false,
    },
  ];
};

export const DenyLists = ({ disableVirtualization }: DenyListsProps) => {
  const navigate = useNavigate();
  const denyLists = useDenyLists();

  const onRowClick = (r: GridRowParams<Row>) => {
    navigate(`/registry/deny-lists/${encodeDenyListName(r.row.payload.name)}`);
  };

  const isLoading = denyLists.isLoading || !denyLists.data;
  if (isLoading) return <Loading />;

  return (
    <PageWrapper pageName="Deny Lists">
      <DataTable
        columns={createHeaders()}
        rows={denyLists.data}
        defaultSortModel={[{ field: 'owner', sort: 'asc' }]}
        rowKey={(a) => a.contractId}
        onRowClick={onRowClick}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
